import { ClockCircleOutlined, CrownFilled, EditOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  InputNumber,
  List,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Tag,
  TimePicker,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import type { AllocateInput, Allocation, AllocationResult, EditAllocationInput } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import {
  DATE,
  DEFAULT_DAILY_MINUTES,
  DISPLAY_DATE,
  homeTeam,
  hours,
  isWeekend,
  projectLabel,
  slotText,
  timeText,
  today,
  useFeedback,
  useProjects,
  UserSelect,
  useTeams,
} from './shared';

export interface AllocationDefaults {
  teamId?: string | null;
  userId?: string;
  projectId?: string;
  from?: string;
  to?: string;
  minutes?: number;
}

export const Warnings: React.FC<{ result: AllocationResult }> = ({ result }) => (
  <Space direction='vertical' style={{ width: '100%' }}>
    {result.warnings.length ? (
      <Alert
        type='warning'
        showIcon
        message='Depasire a programului zilnic'
        description={
          <List
            size='small'
            dataSource={result.warnings}
            renderItem={(warning) => (
              <List.Item>
                {warning.userName} · {dayjs(warning.date).format(DISPLAY_DATE)}: {hours(warning.minutes)} /{' '}
                {hours(warning.capacity)}
              </List.Item>
            )}
          />
        }
      />
    ) : (
      <Alert type='success' showIcon message='Nicio depasire a capacitatii zilnice.' />
    )}
    {!!result.conflicts?.length && (
      <Alert
        type='warning'
        showIcon
        message='Intervale orare suprapuse'
        description={
          <List
            size='small'
            dataSource={result.conflicts}
            renderItem={(conflict) => (
              <List.Item>
                {conflict.userName} · {dayjs(conflict.date).format(DISPLAY_DATE)} · {conflict.projectNumber} /{' '}
                {conflict.otherProjectNumber}: {timeText(conflict.startTime)}–{timeText(conflict.endTime)}
              </List.Item>
            )}
          />
        }
      />
    )}
    <Typography.Text type='secondary'>
      Alocarile fara interval orar sunt verificate doar pentru capacitatea zilnica.
    </Typography.Text>
  </Space>
);

type Preview = { create?: AllocateInput; edit?: EditAllocationInput; result: AllocationResult };
const clock = (value: string) => dayjs(`2000-01-01T${value}`);
const AllocationModal: React.FC<{
  allocation?: Allocation | null;
  defaults?: AllocationDefaults;
  from: string;
  to: string;
  onClose: () => void;
}> = ({ allocation, defaults, from, to, onClose }) => {
  const { data: projects = [] } = useProjects();
  const { data: teams = [] } = useTeams();
  const feedback = useFeedback();
  const [form] = Form.useForm();
  const wholeTeam = Form.useWatch('wholeTeam', form);
  const teamId = Form.useWatch('teamId', form);
  const timed = Form.useWatch('timed', form);
  const projectId = Form.useWatch('projectId', form);
  const period: [dayjs.Dayjs, dayjs.Dayjs] | undefined = Form.useWatch('period', form);
  const [preview, setPreview] = useState<Preview | null>(null);
  const mutation = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const team = teams.find((item) => item.id === teamId),
    project = projects.find((item) => item.id === projectId);
  const dailyHours = (id?: string | null) =>
    (teams.find((item) => item.id === id)?.dailyMinutes || DEFAULT_DAILY_MINUTES) / 60;
  const submit = async () => {
    try {
      if (preview) {
        mutation.mutate(async () => {
          const result = preview.edit
            ? await api.editAllocation({ ...preview.edit, previewOnly: false })
            : await api.allocate({ ...preview.create!, previewOnly: false });
          feedback.success(
            result.warnings.length || result.conflicts?.length
              ? 'Alocari salvate. Avertizarile raman marcate in planificare.'
              : 'Alocari salvate.',
          );
          onClose();
        });
        return;
      }
      const values = await form.validateFields();
      const times = values.timed
        ? { startTime: values.slot[0].format('HH:mm:ss'), endTime: values.slot[1].format('HH:mm:ss') }
        : {};
      const minutes = values.timed ? values.slot[1].diff(values.slot[0], 'minute') : Math.round(values.hours * 60);
      if (allocation) {
        const payload: EditAllocationInput = {
          id: allocation.id,
          projectId: values.projectId,
          teamId: values.teamId || null,
          date: values.date.format(DATE),
          minutes,
          expectedVersion: allocation.rowVersion,
          clearTimeSlot: !values.timed,
          ...times,
        };
        mutation.mutate(async () =>
          setPreview({ edit: payload, result: await api.editAllocation({ ...payload, previewOnly: true }) }),
        );
      } else {
        const payload: AllocateInput = {
          projectId: values.projectId,
          teamId: values.teamId || null,
          wholeTeam: values.wholeTeam,
          userIds: values.userIds || [],
          from: values.period[0].format(DATE),
          to: values.period[1].format(DATE),
          minutesPerDay: minutes,
          weekdaysOnly: values.weekdaysOnly,
          ...times,
        };
        mutation.mutate(async () =>
          setPreview({ create: payload, result: await api.allocate({ ...payload, previewOnly: true }) }),
        );
      }
    } catch (error) {
      feedback.error(error);
    }
  };
  const initialValues = allocation
    ? {
        projectId: allocation.projectId,
        teamId: allocation.teamId,
        date: dayjs(allocation.date),
        hours: allocation.minutes / 60,
        timed: !!allocation.startTime,
        slot:
          allocation.startTime && allocation.endTime
            ? [clock(allocation.startTime), clock(allocation.endTime)]
            : undefined,
      }
    : {
        projectId: defaults?.projectId,
        teamId: defaults?.teamId,
        wholeTeam: !defaults?.userId && !!defaults?.teamId,
        userIds: defaults?.userId ? [defaults.userId] : undefined,
        period: [dayjs(defaults?.from || from), dayjs(defaults?.to || to)],
        hours: defaults?.minutes ? defaults.minutes / 60 : dailyHours(defaults?.teamId),
        timed: false,
        weekdaysOnly: !(defaults?.from && defaults.from === defaults.to && isWeekend(defaults.from)),
      };
  const [start, end] =
    period?.[0] && period?.[1] ? [period[0].format(DATE), period[1].format(DATE)] : [today(), today()];
  const currentMembers = new Set(
    team?.memberships
      .filter((member) => member.validFrom <= end && (!member.validTo || member.validTo > start))
      .map((member) => member.userId),
  );
  const payload = preview?.create || preview?.edit;
  const previewProject = projects.find((item) => item.id === payload?.projectId);
  return (
    <Modal
      open
      title={
        allocation ? (
          <span>
            <EditOutlined /> Editeaza alocarea zilnica
          </span>
        ) : (
          'Alocare noua'
        )
      }
      onCancel={onClose}
      onOk={submit}
      confirmLoading={mutation.isPending}
      okText={preview ? 'Salveaza' : 'Verifica alocarea'}
      cancelText='Renunta'
      width={660}
      destroyOnHidden
    >
      {preview ? (
        <Space direction='vertical' style={{ width: '100%' }}>
          <Typography.Text strong>
            {preview.result.planned} alocari zilnice · {hours(preview.create?.minutesPerDay ?? preview.edit!.minutes)} /
            persoana / zi
          </Typography.Text>
          <Typography.Text>
            {previewProject && projectLabel(previewProject)} ·{' '}
            {teams.find((item) => item.id === payload?.teamId)?.name || 'Alocare directa, fara echipa operationala'}
          </Typography.Text>
          <Typography.Text>{slotText(payload || {})}</Typography.Text>
          {preview.create && (
            <Typography.Text>
              {dayjs(preview.create.from).format(DISPLAY_DATE)} – {dayjs(preview.create.to).format(DISPLAY_DATE)} ·{' '}
              {preview.result.people?.map((person) => person.name).join(', ')}
            </Typography.Text>
          )}
          {preview.edit && (
            <Typography.Text>
              {allocation?.userName} · {dayjs(preview.edit.date).format(DISPLAY_DATE)}
            </Typography.Text>
          )}
          <Warnings result={preview.result} />
          <Button type='link' onClick={() => setPreview(null)}>
            Modifica selectia
          </Button>
        </Space>
      ) : (
        <Form
          form={form}
          layout='vertical'
          initialValues={initialValues}
          onValuesChange={(changed, values) => {
            if (!allocation && changed.teamId && !values.timed)
              form.setFieldsValue({ hours: dailyHours(changed.teamId) });
            if (
              !allocation &&
              (changed.userIds || changed.period) &&
              !values.teamId &&
              !values.wholeTeam &&
              values.userIds?.length === 1
            ) {
              const home = homeTeam(teams, values.userIds[0], values.period?.[0]?.format(DATE) || from);
              if (home?.isActive)
                form.setFieldsValue({ teamId: home.id, ...(!values.timed ? { hours: home.dailyMinutes / 60 } : {}) });
            }
            if (changed.slot?.[0] && changed.slot?.[1])
              form.setFieldsValue({ hours: Math.max(0, changed.slot[1].diff(changed.slot[0], 'minute')) / 60 });
          }}
        >
          {allocation && <Typography.Paragraph strong>{allocation.userName}</Typography.Paragraph>}
          <Form.Item
            name='projectId'
            label='Proiect'
            rules={[{ required: true }]}
            extra={project && `Responsabil: ${project.responsibleName || 'Nedesemnat'}`}
          >
            <Select
              showSearch
              optionFilterProp='label'
              options={projects
                .filter(
                  (item) =>
                    item.id === allocation?.projectId ||
                    item.isActive,
                )
                .map((item) => ({ value: item.id, label: projectLabel(item) }))}
            />
          </Form.Item>
          {!allocation && (
            <>
              <Form.Item name='wholeTeam' label='Persoane'>
                <Radio.Group
                  options={[
                    { value: false, label: 'Tehnicieni selectati' },
                    { value: true, label: 'Echipa intreaga' },
                  ]}
                />
              </Form.Item>
              {!wholeTeam && (
                <Form.Item
                  name='userIds'
                  label='Tehnicieni din orice echipa sau fara echipa'
                  rules={[{ required: true, type: 'array', min: 1 }]}
                >
                  <UserSelect
                    multiple
                    describe={(userId) => `baza: ${homeTeam(teams, userId, start)?.name || 'Fara echipa'}`}
                  />
                </Form.Item>
              )}
            </>
          )}
          <Form.Item
            name='teamId'
            label={wholeTeam ? 'Echipa de alocat' : 'Echipa operationala (optional)'}
            rules={wholeTeam ? [{ required: true }] : []}
            extra={
              <Space size={4} wrap>
                {team && (
                  <Tag bordered={false} icon={<ClockCircleOutlined />}>
                    {hours(team.dailyMinutes)} / zi
                  </Tag>
                )}
                {team?.leaderName && (
                  <Tag bordered={false} color='gold' icon={<CrownFilled />}>
                    {team.leaderName}
                  </Tag>
                )}
                {wholeTeam ? (
                  <Typography.Text type='secondary'>{currentMembers.size} membri in perioada</Typography.Text>
                ) : (
                  <Typography.Text type='secondary'>
                    Apartenenta la echipa de baza ramane neschimbata. Fara selectie = alocare directa pe proiect.
                  </Typography.Text>
                )}
              </Space>
            }
          >
            <Select
              allowClear={!wholeTeam}
              placeholder='Fara echipa operationala'
              options={teams.filter((item) => item.isActive).map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          {!allocation && (
            <>
              <Form.Item name='period' label='Perioada' rules={[{ required: true }]}>
                <DatePicker.RangePicker format={DISPLAY_DATE} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name='weekdaysOnly' label='Doar luni – vineri' valuePropName='checked'>
                <Switch />
              </Form.Item>
            </>
          )}
          {allocation && (
            <Form.Item name='date' label='Data' rules={[{ required: true }]}>
              <DatePicker format={DISPLAY_DATE} />
            </Form.Item>
          )}
          <Form.Item name='timed' label='Interval orar' valuePropName='checked'>
            <Switch />
          </Form.Item>
          {timed && (
            <Form.Item
              name='slot'
              label='Ora inceput – ora final'
              rules={[
                {
                  validator: (_, value) =>
                    !value?.[0] || !value?.[1]
                      ? Promise.reject(new Error('Completati ora de inceput si ora finala.'))
                      : value[1].isAfter(value[0])
                        ? Promise.resolve()
                        : Promise.reject(new Error('Ora finala trebuie sa fie dupa ora de inceput, in aceeasi zi.')),
                },
              ]}
            >
              <TimePicker.RangePicker format='HH:mm' minuteStep={5} needConfirm={false} order={false} />
            </Form.Item>
          )}
          <Form.Item name='hours' label='Ore / persoana / zi' rules={[{ required: true }]}>
            <InputNumber disabled={!!timed} min={1 / 60} max={24} step={0.25} precision={2} addonAfter='ore' />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
export default AllocationModal;
